import { useEffect, useRef, useCallback, useState } from 'react';

interface UseInfiniteScrollProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  /**
   * Scroll container element to use as IntersectionObserver root.
   * Pass the mounted element (not a ref) so changes trigger the effect.
   */
  root?: HTMLElement | null;
}

export const useInfiniteScroll = ({ hasMore, isLoading, onLoadMore, root }: UseInfiniteScrollProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  // IMPORTANT: The sentinel element is conditionally rendered (e.g. only in "All Apps").
  // If we only store it in a ref, the observer effect can run before the element mounts
  // and never re-observe it. Using state guarantees the observer re-initializes when the
  // sentinel appears/disappears.
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const loadMoreRef = useCallback((el: HTMLDivElement | null) => {
    setSentinelEl(el);
  }, []);
  const lastCallTime = useRef(0);
  const isProcessing = useRef(false);
  
  // ✅ Store callback in ref - prevents dependency chain from Convex's unstable loadMore
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  
  // ✅ Store hasMore/isLoading in refs for stable callback access
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  hasMoreRef.current = hasMore;
  isLoadingRef.current = isLoading;

  // ✅ Stable intersection handler - reads from refs, no dependencies
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0];
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime.current;
    
    // Guard: not intersecting
    if (!target.isIntersecting) return;
    
    // Guard: no more content or already loading (read from refs for current values)
    if (!hasMoreRef.current || isLoadingRef.current) return;
    
    // Guard: already processing or called too recently (debounce)
    if (isProcessing.current || timeSinceLastCall < 1000) return;
    
    // All guards passed - execute load
    isProcessing.current = true;
    lastCallTime.current = now;
    
    // Call the latest callback from ref
    onLoadMoreRef.current();
    
    // Reset processing flag after delay
    setTimeout(() => {
      isProcessing.current = false;
    }, 500);
  }, []); // ✅ Empty deps - stable forever

  useEffect(() => {
    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Don't create observer if no more content or currently loading
    if (!hasMore || isLoading || !sentinelEl) {
      return;
    }

    // Create observer with scroll container as root (if provided)
    const options: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '100px',
      root: root || null, // Use scroll container as root
    };

    observerRef.current = new IntersectionObserver(handleIntersection, options);

    observerRef.current.observe(sentinelEl);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [hasMore, isLoading, handleIntersection, root, sentinelEl]);

  return {
    // Use as: <div ref={loadMoreRef} />
    // Re-initializes observation whenever the sentinel mounts/unmounts.
    loadMoreRef,
  };
};
